import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Heart, TrendingUp, Users, Sparkles, CheckCircle, MapPin, Mountain, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useCommunityVotingData } from '@/hooks/useCommunityVotingData';

interface VotingModalProps {
  trigger: React.ReactNode;
}

export const CommunityVotingModal = ({ trigger }: VotingModalProps) => {
  const { toast } = useToast();
  const { data: votingData, isLoading } = useCommunityVotingData();
  
  const [votes, setVotes] = useState<Record<string, number>>({
    'nature': 0,
    'movement': 0,
    'creative': 0,
    'food': 0,
    'community': 0,
    'work': 0
  });
  
  const [userVotes, setUserVotes] = useState<Set<string>>(new Set());
  const [totalVoters, setTotalVoters] = useState(0);
  const [bonusAnswer, setBonusAnswer] = useState<'city' | 'retreat' | null>(null);
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'voting' | 'submitted'>('voting');
  const [sessionId] = useState(() => crypto.randomUUID());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    {
      id: 'nature',
      emoji: '🌲',
      title: 'Nature & Mindfulness',
      events: ['Shinrin Yoku (forest bathing)', 'Silent Walks / Mindful Hiking', 'Outdoor Meditation Circles', 'Digital Detox Retreat'],
      color: 'from-emerald-500/20 to-green-500/20 border-emerald-200'
    },
    {
      id: 'movement',
      emoji: '🧘',
      title: 'Movement & Body',
      events: ['Yoga Retreat (Hatha, Vinyasa, Yin)', 'Breathwork & Somatic Practices', 'Ecstatic Dance', 'Qi Gong / Tai Chi in Nature'],
      color: 'from-purple-500/20 to-violet-500/20 border-purple-200'
    },
    {
      id: 'creative',
      emoji: '🎶',
      title: 'Creative & Musical',
      events: ['Handpan / Sound Healing Workshop', 'Drum Circles', 'Nature-Inspired Art Workshop', 'Storytelling by Firelight'],
      color: 'from-orange-500/20 to-red-500/20 border-orange-200'
    },
    {
      id: 'food',
      emoji: '🍲',
      title: 'Food & Rituals',
      events: ['Slow Cooking with Local Ingredients', 'Plant-Based Foraging & Cooking Class', 'Tea Ceremony in the Forest', 'Fire Rituals & Seasonal Celebrations'],
      color: 'from-amber-500/20 to-yellow-500/20 border-amber-200'
    },
    {
      id: 'community',
      emoji: '👥',
      title: 'Community & Connection',
      events: ['Men\'s Circle', 'Women\'s Circle', 'Family Retreat (with kids\' activities)', 'Couples Retreat (reconnection practices)'],
      color: 'from-blue-500/20 to-cyan-500/20 border-blue-200'
    },
    {
      id: 'work',
      emoji: '💼',
      title: 'Work & Growth',
      events: ['Corporate Digital Detox Workshop', 'Leadership & Mindfulness in Nature', 'Team-Building Retreats'],
      color: 'from-slate-500/20 to-gray-500/20 border-slate-200'
    }
  ];

  // Process voting data when it changes
  useEffect(() => {
    if (votingData) {
      // Count occurrences of each category
      const categoryCounts = {
        'nature': 0,
        'movement': 0,
        'creative': 0,
        'food': 0,
        'community': 0,
        'work': 0
      };

      votingData.forEach(record => {
        record.experience_categories?.forEach(category => {
          if (categoryCounts.hasOwnProperty(category)) {
            categoryCounts[category]++;
          }
        });
      });

      // Update state with new counts
      setVotes(categoryCounts);
      setTotalVoters(votingData.length);
    }
  }, [votingData]);

  const handleSubmitVotes = async () => {
    if (userVotes.size === 0) {
      toast({
        title: "No votes selected",
        description: "Please vote for at least one category before submitting.",
        variant: "destructive",
      });
      return;
    }

    if (!bonusAnswer) {
      toast({
        title: "Please answer the bonus question",
        description: "Your preference helps us plan better experiences for you.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const experienceCategories = Array.from(userVotes);
      const eventPreference = bonusAnswer === 'city' ? 'city_events' : 'weekend_retreats';

      // Use edge function with rate limiting instead of direct DB insert
      const { data: result, error } = await supabase.functions.invoke('submit-vote', {
        body: {
          experienceCategories,
          eventPreference,
          sessionId
        }
      });

      if (error) {
        // Handle rate limiting error
        if (error.message?.includes('429') || error.message?.includes('Too many')) {
          toast({
            title: "Too many vote submissions",
            description: "You've reached the daily limit. Thank you for your participation!",
            variant: "destructive",
          });
        }
        // Check if it's a duplicate vote (409 conflict)
        else if (error.message?.includes('409') || error.message?.includes('already')) {
          toast({
            title: "Already voted",
            description: "You have already submitted your vote. Thank you for your participation!",
            variant: "destructive",
          });
        } else {
          throw error;
        }
      } else {
        setStep('submitted');
        toast({
          title: "Vote submitted successfully!",
          description: "Thank you for helping shape Mystic Forest!",
        });
      }
    } catch (error) {
      toast({
        title: "Error submitting vote",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVote = (categoryId: string) => {
    if (userVotes.has(categoryId)) return;
    
    // Just track user's votes locally, don't increment vote count
    // Vote counts will be updated via real-time subscription
    setUserVotes(prev => new Set([...prev, categoryId]));
  };

  const getVotePercentage = (categoryId: string) => {
    const totalVotes = Object.values(votes).reduce((sum, count) => sum + count, 0);
    return Math.round((votes[categoryId] / totalVotes) * 100);
  };

  const getUserVotedCategories = () => {
    return categories.filter(cat => userVotes.has(cat.id));
  };

  const sortedCategories = [...categories].sort((a, b) => votes[b.id] - votes[a.id]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif text-center mb-2">
            Help Shape Mystic Forest
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            {step === 'voting' && "Your voice matters! Vote for the experiences you'd love to see at Mystic Forest."}
            {step === 'submitted' && "Thank you for your participation! We'll keep you updated on our progress."}
          </DialogDescription>
        </DialogHeader>

        {step === 'voting' && (
          <div className="space-y-6 mt-6">
            {/* Community Stats */}
            <div className="bg-muted/50 rounded-lg p-6 text-center">
            <div className="flex justify-center items-center gap-8 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-accent" />
                <span>{totalVoters} community member{totalVoters === 1 ? '' : 's'}</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-accent" />
                <span>Live voting results</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-accent" />
                <span>Building together</span>
              </div>
            </div>
          </div>

          {/* Voting Categories */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading voting results...</p>
            </div>
          ) : (
            <div className="grid gap-4">
            {sortedCategories.map((category, index) => {
              const hasVoted = userVotes.has(category.id);
              const percentage = getVotePercentage(category.id);
              
              return (
                <div key={category.id} className={`relative border rounded-lg p-6 bg-gradient-to-r ${category.color} transition-all duration-300 ${hasVoted ? 'ring-2 ring-accent' : 'hover:shadow-md'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{category.emoji}</span>
                      <div>
                        <h3 className="font-semibold text-lg text-foreground">{category.title}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{votes[category.id]} votes</span>
                          <span>•</span>
                          <span>{percentage}% of community</span>
                          {index === 0 && <span className="text-accent font-medium">• Most Popular!</span>}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleVote(category.id)}
                      disabled={hasVoted}
                      variant={hasVoted ? "secondary" : "outline"}
                      size="sm"
                      className="min-w-20"
                    >
                      {hasVoted ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <Heart className="w-4 h-4" />
                      )}
                      {hasVoted ? 'Voted' : 'Vote'}
                    </Button>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-background/50 rounded-full h-2 mb-4">
                    <div 
                      className="bg-accent h-2 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>

                  {/* Events list */}
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground font-medium">Example experiences:</p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm text-muted-foreground">
                      {category.events.map((event, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="w-1 h-1 bg-accent rounded-full"></span>
                          {event}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
          )}

          {/* Voting confirmation and bonus question */}
          {userVotes.size > 0 && (
            <div className="sticky top-0 bg-background/95 backdrop-blur-sm border rounded-lg p-6 space-y-6 shadow-lg">
              {/* Voting Summary */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-accent" />
                  <h4 className="font-semibold text-foreground">Thanks for voting!</h4>
                </div>
                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  {getUserVotedCategories().map(category => (
                    <span 
                      key={category.id}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-accent/10 text-accent rounded-full text-sm border border-accent/20"
                    >
                      <span>{category.emoji}</span>
                      {category.title}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  You've voted for {userVotes.size} categor{userVotes.size === 1 ? 'y' : 'ies'}
                </p>
              </div>

              {/* Bonus Question */}
              <div className="border-t pt-6">
                <div className="text-center mb-4">
                  <h4 className="font-semibold text-foreground mb-2">🎁 Bonus Question</h4>
                  <p className="text-sm text-muted-foreground">
                    What format would you prefer for your Mystic Forest experiences?
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                    onClick={() => setBonusAnswer('city')}
                    variant={bonusAnswer === 'city' ? 'default' : 'outline'}
                    className="h-auto p-4 flex flex-col items-center gap-2 text-center relative"
                  >
                    <MapPin className="w-5 h-5" />
                    <div>
                      <div className="font-medium text-sm">Short City Events</div>
                      <div className="text-xs opacity-75">2-4 hour experiences</div>
                    </div>
                    {bonusAnswer === 'city' && (
                      <CheckCircle className="w-4 h-4 absolute top-2 right-2 text-accent" />
                    )}
                  </Button>
                  
                  <Button
                    onClick={() => setBonusAnswer('retreat')}
                    variant={bonusAnswer === 'retreat' ? 'default' : 'outline'}
                    className="h-auto p-4 flex flex-col items-center gap-2 text-center relative"
                  >
                    <Mountain className="w-5 h-5" />
                    <div>
                      <div className="font-medium text-sm">Weekend Retreats</div>
                      <div className="text-xs opacity-75">Full immersion away</div>
                    </div>
                    {bonusAnswer === 'retreat' && (
                      <CheckCircle className="w-4 h-4 absolute top-2 right-2 text-accent" />
                    )}
                  </Button>
                </div>

                 {bonusAnswer && (
                   <div className="mt-4 p-3 bg-accent/5 rounded-lg border border-accent/10 text-center">
                     <p className="text-sm text-muted-foreground">
                       ✨ Perfect! We'll prioritize <span className="font-medium text-foreground">
                         {bonusAnswer === 'city' ? 'short city events' : 'weekend retreats'}
                       </span> for you.
                     </p>
                   </div>
                 )}

                 {/* Submit Button */}
                 <div className="mt-6 text-center">
                   <Button 
                     onClick={handleSubmitVotes}
                     disabled={isSubmitting || userVotes.size === 0 || !bonusAnswer}
                     className="px-8 py-3 text-lg"
                   >
                     {isSubmitting ? 'Submitting...' : 'Submit My Votes'}
                   </Button>
                 </div>
                 </div>
               </div>
             )}
           </div>
        )}

        {step === 'submitted' && (
          <div className="space-y-6 mt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
              <p className="text-muted-foreground mb-4">
                Your votes have been recorded. You've helped shape the future of Mystic Forest!
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};